/**
 * Global Fee Schedule API
 * Centralized API calls for global fee schedule functionality
 */

import { GlobalFeeScheduleMetadata, GlobalFeeSchedule, State, Locality } from '../../types/globalFeeScheduleTypes';
import { SERVER_URL } from '../constants/api';
import { headersAuthSendReceiveJson } from '../headers';

// ============================================================================
// TYPES
// ============================================================================

export interface GlobalConfigCheckResponse {
    isGlobal: boolean;
    isRBRVS: boolean;
    metadata?: GlobalFeeScheduleMetadata;
}

export interface FeeScheduleSearchRequest {
    keyword: string;
    directoryId: number;
    useGlobalFeeSchedule: boolean;
}

export interface FeeScheduleSearchResponse {
    id: number;
    name: string;
    path: string;
    isDirectory: boolean;
    isRBRVS: boolean;
    isGlobal: boolean;
    count: number;
}

export interface GPCILocalityData {
    state: string;
    gpci: string;
    locality: string;
}

// ============================================================================
// CACHING
// ============================================================================

// Shared cache for GPCI locality data to prevent multiple API calls
let gpciLocalityCache: GPCILocalityData[] | null = null;
let gpciLocalityPromise: Promise<GPCILocalityData[]> | null = null;

/**
 * Clear GPCI locality cache (useful for testing or manual refresh)
 */
export const clearGpciLocalityCache = (): void => {
    gpciLocalityCache = null;
    gpciLocalityPromise = null;
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Fetch Global Fee Schedules from backend
 * 
 * @param keyword - Search keyword
 * @param directoryId - Directory ID to search within
 * @returns Promise<GlobalFeeSchedule[]>
 */
export const fetchGlobalFeeSchedules = async (
    keyword: string = '',
    directoryId: number = 0
): Promise<GlobalFeeSchedule[]> => {
    const requestBody: FeeScheduleSearchRequest = {
        keyword,
        directoryId,
        useGlobalFeeSchedule: true
    };

    const response = await fetch(`${SERVER_URL}/api/v2/cms/feeschedule/search`, {
        method: 'POST',
        headers: headersAuthSendReceiveJson(),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw new Error('Failed to fetch global fee schedules');
    }

    const data: FeeScheduleSearchResponse[] = await response.json();
    
    // Transform backend response to GlobalFeeSchedule format
    return data
        .filter(item => !item.isDirectory && item.isGlobal) // Only non-directory global schedules
        .map(item => ({
            id: item.id,
            name: item.name,
            description: `${item.name} - ${item.path}`,
            isDirectory: item.isDirectory,
            fee_schedule_type_id: item.isRBRVS ? 1 : 2, // 1 for RBRVS, 2 for CLAB
            directory_id: directoryId || null,
            type_of_schedule: item.isRBRVS ? 2 : 1, // 2 for RBRVS, 1 for CLAB
            updated_on: new Date().toISOString(),
            updated_by: 'system',
            isGlobal: item.isGlobal,
            isRBRVS: item.isRBRVS // Use backend value directly
        }));
};

/**
 * Check if a fee schedule is global and requires configuration
 * 
 * @param scheduleId - Fee schedule ID
 * @returns Promise<GlobalConfigCheckResponse>
 */
export const checkRequiresGlobalConfig = async (scheduleId: number): Promise<GlobalConfigCheckResponse> => {
    try {
        const response = await fetch(`${SERVER_URL}/api/v2/cms/feeschedule/search`, {
            method: 'POST',
            headers: headersAuthSendReceiveJson(),
            body: JSON.stringify({
                keyword: '',
                directoryId: 0,
                useGlobalFeeSchedule: true
            })
        });

        if (!response.ok) {
            throw new Error('Failed to check global config requirement');
        }

        const data: FeeScheduleSearchResponse[] = await response.json();
        const schedule = data.find(s => s.id === scheduleId);

        if (!schedule || !schedule.isGlobal) {
            return {
                isGlobal: false,
                isRBRVS: false
            };
        }

        // Use the isRBRVS value directly from the schedule
        const isRBRVS = schedule.isRBRVS;

        // If it's a global RBRVS schedule, return metadata with regional fields
        if (schedule.isGlobal && isRBRVS) {
            return {
                isGlobal: true,
                isRBRVS: true,
                metadata: {
                    id: scheduleId,
                    name: schedule.name,
                    description: `${schedule.name} - Global Fee Schedule`,
                    isRBRVS: true,
                    requires_state: true,
                    requires_locality: true,
                    requires_gpci: true,
                    rate_types: [
                        { name: 'Non-Facility', column: 'rate', description: 'Non-Facility Rate' },
                        { name: 'Facility', column: 'rate_1', description: 'Facility Rate' }
                    ]
                }
            };
        }

        // Return for CLAB schedules - no regional fields required
        return {
            isGlobal: true,
            isRBRVS: false
        };
    } catch (error) {
        console.error('[checkRequiresGlobalConfig] Error:', error);
        throw error;
    }
};

/**
 * Fetch schedule metadata including requirements
 * 
 * @param scheduleId - Fee schedule ID
 * @returns Promise<GlobalFeeScheduleMetadata | null>
 */
export const fetchScheduleMetadata = async (scheduleId: number): Promise<GlobalFeeScheduleMetadata | null> => {
    const configCheck = await checkRequiresGlobalConfig(scheduleId);
    return configCheck.metadata || null;
};

/**
 * Fetch GPCI Locality data from backend (with caching)
 * This single API provides all state, GPCI, and locality data
 */
export const fetchGPCILocalityData = async (): Promise<GPCILocalityData[]> => {
    // Return cached data if available
    if (gpciLocalityCache) {
        return gpciLocalityCache;
    }
    
    // If a fetch is already in progress, wait for it
    if (gpciLocalityPromise) {
        return gpciLocalityPromise;
    }
    
    // Start new fetch
    gpciLocalityPromise = fetch(`${SERVER_URL}/api/v1/cms/gpcilocality`, {
        method: 'GET',
        headers: headersAuthSendReceiveJson()
    })
    .then(response => {
        if (!response.ok) {
            throw new Error('Failed to fetch GPCI locality data');
        }
        return response.json();
    })
    .then((data: GPCILocalityData[]) => {
        gpciLocalityCache = data;
        gpciLocalityPromise = null;
        return data;
    })
    .catch(err => {
        gpciLocalityPromise = null;
        throw err;
    });
    
    return gpciLocalityPromise;
};

/**
 * Fetch available states from GPCI locality data
 * Returns unique list of states
 */
export const fetchStates = async (scheduleId: number): Promise<State[]> => {
    const data = await fetchGPCILocalityData();
    
    // Get unique states
    const uniqueStates = Array.from(new Set(data.map(item => item.state)))
        .sort()
        .map(state => ({
            code: state,
            name: state
        }));
    
    return uniqueStates;
};

/**
 * Fetch GPCI values for a specific state
 * Returns unique GPCI values for the selected state
 */
export const fetchGPCIValues = async (scheduleId: number, stateCode: string): Promise<string[]> => {
    const data = await fetchGPCILocalityData();
    
    // Filter by state and get unique GPCI values
    const gpciValues = Array.from(
        new Set(
            data
                .filter(item => item.state === stateCode)
                .map(item => item.gpci)
        )
    ).sort();
    
    return gpciValues;
};

/**
 * Fetch localities for a specific state and GPCI
 * Returns localities filtered by both state and GPCI
 */
export const fetchLocalities = async (
    scheduleId: number, 
    stateCode: string, 
    gpciValue?: string
): Promise<Locality[]> => {
    const data = await fetchGPCILocalityData();
    
    // Filter by state and optionally by GPCI
    const filtered = data.filter(item => {
        if (item.state !== stateCode) return false;
        if (gpciValue && item.gpci !== gpciValue) return false;
        return true;
    });
    
    // Map to Locality interface
    return filtered.map(item => ({
        code: item.locality,
        name: `Locality ${item.locality}`,
        state: item.state,
        gpci: item.gpci
    }));
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export interface RegionalFieldValues {
    selectedState?: string | null;
    selectedLocality?: string | null;
    selectedGpci?: string | null;
}

export interface RegionalFieldErrors {
    selectedState?: string;
    selectedLocality?: string;
    selectedGpci?: string;
}

/**
 * Validate regional fields (State, Locality, GPCI) for global RBRVS fee schedules
 * This is a generic validation function that can be used across different sections
 * 
 * @param values - Object containing selectedState, selectedLocality, selectedGpci
 * @param options - Optional configuration for error messages
 * @returns Object with errors (empty if all valid)
 */
export const validateRegionalFields = (
    values: RegionalFieldValues,
    options?: {
        stateErrorMessage?: string;
        localityErrorMessage?: string;
        gpciErrorMessage?: string;
    }
): RegionalFieldErrors => {
    const errors: RegionalFieldErrors = {};
    const stateError = options?.stateErrorMessage || 'State is required';
    const localityError = options?.localityErrorMessage || 'Locality is required';
    const gpciError = options?.gpciErrorMessage || 'GPCI is required';
    
    if (!values.selectedState) {
        errors.selectedState = stateError;
    }
    if (!values.selectedLocality) {
        errors.selectedLocality = localityError;
    }
    if (!values.selectedGpci) {
        errors.selectedGpci = gpciError;
    }
    
    return errors;
};

/**
 * Check if regional fields have any validation errors
 * 
 * @param errors - RegionalFieldErrors object
 * @returns true if there are any errors
 */
export const hasRegionalFieldErrors = (errors: RegionalFieldErrors): boolean => {
    return Boolean(errors.selectedState || errors.selectedLocality || errors.selectedGpci);
};

